// src/components/DelegationTasks.js
import React, { useEffect, useMemo, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { getISOWeek } from 'date-fns';
import './delegation_task.css';
import AppSidebar from './AppSidebar';

// --- Custom SVGs ---
const DocIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>);
const SearchIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>);
const FilterIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>);
const CalendarIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>);
const TimelineIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>);
const UserIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>);
const PlusIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
const CopyIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>);


// --- New KPI Icons ---
const CubeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const WarningTriangleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
// --- Utility Functions ---
const avatarColors = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const getAvatarColor = (name) => {
  if (!name) return '#d97706'; // default
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const index = Math.abs(hash) % avatarColors.length;
  return avatarColors[index];
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getStatusBadgeClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('progress')) return 'dt-badge-blue';
  if (s.includes('completed')) return 'dt-badge-green';
  if (s.includes('overdue')) return 'dt-badge-red';
  return 'dt-badge-amber';
};

const getMinRevisionDate = (...baseDateStrings) => {
  const validDates = baseDateStrings
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()));

  if (validDates.length === 0) return '';

  const latestDate = validDates.reduce((latest, current) =>
    current > latest ? current : latest
  );

  latestDate.setDate(latestDate.getDate() + 1);

  return `${latestDate.getFullYear()}-${String(latestDate.getMonth() + 1).padStart(2, '0')}-${String(latestDate.getDate()).padStart(2, '0')}`;
};

// --- Custom Inline Calendar Component ---
const CustomCalendar = ({ value, minDate, onSelect }) => {
  const getInitialViewDate = (baseValue, baseMinDate) => {
    const minDateValue = baseMinDate ? new Date(baseMinDate) : null;

    if (minDateValue && !Number.isNaN(minDateValue.getTime())) {
      return new Date(minDateValue.getFullYear(), minDateValue.getMonth(), 1);
    }

    return baseValue ? new Date(baseValue) : new Date();
  };

  const [viewDate, setViewDate] = useState(() => getInitialViewDate(value, minDate));

  useEffect(() => {
    setViewDate(getInitialViewDate(value, minDate));
  }, [value, minDate]);

  const minStr = minDate || '';

  const handlePrev = (e) => {
    e.preventDefault();
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNext = (e) => {
    e.preventDefault();
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const firstDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();

  const days = Array(firstDay).fill(null);
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), i));
  }

  const formatYMD = (d) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <div className="dt-custom-cal" onMouseDown={(e) => e.preventDefault()}>
      <div className="dt-cal-header">
        <button type="button" onClick={handlePrev}>‹</button>
        <span>{viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
        <button type="button" onClick={handleNext}>›</button>
      </div>
      <div className="dt-cal-grid">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <div key={d} className="dt-cal-day-label">{d}</div>
        ))}
        {days.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} />;
          const ymd = formatYMD(d);
          const isPast = minStr && ymd < minStr;
          const isSelected = value === ymd;

          return (
            <button
              key={i}
              type="button"
              className={`dt-cal-day ${isPast ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
              // REMOVED: disabled={isPast} so we can intercept the click
              onClick={(e) => {
                e.preventDefault();
                if (isPast) {
                  toast.error('Cannot select this date. It must be after the previous deadline.');
                } else {
                  onSelect(ymd);
                }
              }}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};


// --- Filter Date Range Picker (matches the Checklist dashboard) ---
const DelegationDateRangePicker = ({ startDate, endDate, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const [step, setStep] = useState(startDate && !endDate ? 'end' : 'start');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keep the two-click flow sane after a Reset
  useEffect(() => {
    if (!startDate && !endDate) setStep('start');
  }, [startDate, endDate]);

  const nextMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
  const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();
  const parseDateLocal = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-');
    return new Date(y, m - 1, d);
  };
  const toStr = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const formatDisplay = (s) => {
    if (!s) return 'MM/DD/YYYY';
    const [y, m, d] = s.split('-');
    return `${m}/${d}/${y}`;
  };

  const handleDayClick = (date) => {
    const dateString = toStr(date);
    if (step === 'start') {
      onDateChange(dateString, '');
      setStep('end');
    } else {
      const start = parseDateLocal(startDate);
      if (start && date < start) onDateChange(dateString, startDate);
      else onDateChange(startDate, dateString);
      setStep('start');
      setIsOpen(false);
    }
  };

  const renderCalendar = (year, month, position) => {
    const days = [];
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));

    return (
      <div className="dt-range-pane">
        <div className="dt-range-cal-header">
          {position === 'left' ? <button type="button" onClick={handlePrevMonth}>&lt;</button> : <span />}
          <span>{new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
          {position === 'right' ? <button type="button" onClick={handleNextMonth}>&gt;</button> : <span />}
        </div>
        <div className="dt-range-grid">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => <div key={idx} className="dt-range-day-name">{d}</div>)}
          {days.map((date, i) => {
            if (!date) return <div key={`e-${i}`} className="dt-range-cell empty" />;
            const dateStr = toStr(date);
            const isStart = startDate === dateStr;
            const isEnd = endDate === dateStr;
            let inRange = false;
            if (startDate && endDate) {
              inRange = date > parseDateLocal(startDate) && date < parseDateLocal(endDate);
            } else if (startDate && hoverDate && step === 'end') {
              const start = parseDateLocal(startDate);
              inRange = (date > start && date < hoverDate) || (date < start && date > hoverDate);
            }
            let cls = 'dt-range-cell';
            if (isStart) cls += ' range-start';
            if (isEnd) cls += ' range-end';
            if (inRange) cls += ' in-range';
            return (
              <div key={i} className={cls} onClick={() => handleDayClick(date)} onMouseEnter={() => setHoverDate(date)}>
                {date.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="dt-range-wrapper" ref={wrapperRef}>
      <div className={`dt-range-input ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <CalendarIcon />
        <span>{formatDisplay(startDate)} &ndash; {formatDisplay(endDate)}</span>
      </div>
      {isOpen && (
        <div className="dt-range-popup">
          {renderCalendar(viewDate.getFullYear(), viewDate.getMonth(), 'left')}
          <div className="dt-range-divider" />
          {renderCalendar(nextMonth.getFullYear(), nextMonth.getMonth(), 'right')}
        </div>
      )}
    </div>
  );
};


const getAvatarInitials = (name) => {
  if (!name) return 'U';
  const parts = String(name).trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name).substring(0, 2).toUpperCase();
};

const getEmployeeLabel = (employee) => {
  const name = employee.emp_name || employee.username || `Emp ${employee.id}`;
  const details = [`ID: ${employee.id}`];
  if (employee.designation) details.push(employee.designation);
  return `${name} • ${details.join(' | ')}`;
};

const generateTaskId = () => {
  const now = new Date();
  const prefix = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`;
  const random = Math.floor(100 + Math.random() * 900);
  return parseInt(`${prefix}${random}`, 10);
};

const isAdminRole = (userObj) => {
  const role = userObj?.role;
  // Check for numeric role IDs (1 = admin, 2 = MIS) or string role names
  if (typeof role === 'number') {
    return role === 1 || role === 2;
  }
  const roleStr = String(role || '').toLowerCase();
  return roleStr === 'admin' || roleStr === 'administrator' || roleStr === 'mis';
};

function getInitials(user) {  
  const source = user?.emp_name || user?.username || 'User';
  return source.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
}

function getUserColor(userName) {
  if (!userName) return '#5b6ee1';
  let hash = 0;
  for (let i = 0; i < userName.length; i++) {
    hash = ((hash << 5) - hash) + userName.charCodeAt(i);
    hash = hash & hash; 
  }
  const colors = ['#5b6ee1', '#ec4899', '#f97316', '#06b6d4', '#8b5cf6', '#10b981', '#f43f5e', '#6366f1', '#14b8a6', '#d97706'];
  return colors[Math.abs(hash) % colors.length];
}

export default function DelegationTasks({ user, onLogout, loggingOut, onNavigateToDashboard, onNavigateToTasks, onNavigateToReturns, onNavigateToFMS,onNavigateToAllTasks,onNavigateToResourceControl,onNavigateToLogging }) {
  const [activeTab, setActiveTab] = useState('list');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const hasAccess = user?.role === 1 || user?.role === 2;

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, duplicate_tasks: 0 });
  const [delegations, setDelegations] = useState([]);
  const [employees, setEmployees] = useState([]);

  // State for inline editing
  const [editingCell, setEditingCell] = useState({ id: null, field: null });
  // --- Auto-open Calendar Logic ---
  const dateInputRef = useRef(null);
const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);

const isLockedDuplicateTask = (task) => {
  if (!task) return false;

  const isRedTask = String(task?.color || '').trim().toLowerCase() === 'dt-row-red';
  const hasCopy = delegations.some(
    (other) => other?.task_id === task?.task_id && other?.delegation_id !== task?.delegation_id
  );

  return isRedTask && hasCopy;
};

 useEffect(() => {
      document.title = 'Delegation Tasks';
    }, []);



  useEffect(() => {
    if (editingCell.id && (editingCell.field === 'revision_date_1' || editingCell.field === 'revision_date_2')) {
      // Small timeout ensures the input is rendered and focused before triggering the picker
      const timer = setTimeout(() => {
        if (dateInputRef.current) {
          try {
            dateInputRef.current.showPicker();
          } catch (e) {
            console.warn('Browser does not support showPicker()', e);
          }
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [editingCell]);
  const [editValue, setEditValue] = useState('');
// Add these new states below your other states
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  // Update formData to include department_id
  const [formData, setFormData] = useState({
    task_description: '',
    assigned_to: '',
    department_id: '', // ADDED THIS
    due_date: '',
    link: '',
  });

  // Add the fetch function
  const loadDepartments = async () => {
    setDepartmentsLoading(true);
    try {
      const response = await fetch('/api/delegation-tasks/departments', { credentials: 'include' });
      const data = await response.json();
      if (data.success) setDepartments(data.departments || []);
    } catch (error) {
      toast.error('Failed to load departments');
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // Update your useEffect to call loadDepartments
  useEffect(() => {
    loadDelegations();
    loadEmployees();
    loadDepartments(); // ADDED THIS
  }, []);


  // Add after your state declarations
const hasDelegationTasksAccess = () => {
  if (!user || !user.resource_access) {
    return false;
  }
  return user.resource_access.Delegation_tasks === true;
};

const accessGranted = hasDelegationTasksAccess();

const loadDelegations = async (silent = false) => {
    if (!silent) setLoading(true); // Only show spinner if not a silent refresh
    try {
      const response = await fetch('/api/delegation-tasks', { 
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${user?.authToken}`,
          'Content-Type': 'application/json',
          'X-User-Id': String(user?.id || user?.emp_id || ''),
          'X-User-Role': String(user?.role || '')
        }
      });
      const data = await response.json();
      if (data.success) {
        setDelegations(data.tasks || []);
        setStats(data.stats || { total: 0, pending: 0, completed: 0, duplicate_tasks: 0 });
      } else throw new Error(data.message);
    } catch (error) {
      toast.error('Failed to load delegation tasks');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const response = await fetch('/api/delegation-tasks/employees', { credentials: 'include' });
      const data = await response.json();
      if (data.success) setEmployees(data.employees || []);
      else throw new Error(data.message);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setEmployeesLoading(false);
    }
  };

  useEffect(() => {
    loadDelegations();
    loadEmployees();
  }, []);

const isDeferredStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'Shifted' || normalized === 'deffered' || normalized === 'overdue';
};

const getTaskStatusKey = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  return isDeferredStatus(status) ? 'Shifted' : normalized;
};

const filteredDelegations = useMemo(() => {
  return delegations.filter((d) => {
    if (!isAdminRole(user)) {
      const currentUserId = user?.emp_id || user?.id;
      if (String(d.assigned_to) !== String(currentUserId) && String(d.assigned_by) !== String(currentUserId)) {
        return false;
      }
    }

    const q = searchTerm.trim().toLowerCase();
    let matchesSearch = true;
    if (q) {
      matchesSearch = (
        String(d.delegation_id || '').toLowerCase().includes(q) ||
        String(d.task_id || '').toLowerCase().includes(q) ||
        String(d.task_description || '').toLowerCase().includes(q) ||
        String(d.assigned_to_name || '').toLowerCase().includes(q) ||
        String(d.assigned_by_name || '').toLowerCase().includes(q) ||
        String(d.status || '').toLowerCase().includes(q)
      );
    }

    const matchesAssigned = filterAssignedTo === 'All' || String(d.assigned_to) === String(filterAssignedTo);
    const matchesStatus = filterStatus === 'All' || getTaskStatusKey(d.status) === filterStatus.toLowerCase();

        let matchesDate = true;
    if (dateFrom || dateTo) {
      // Use the task's LATEST effective date: due_date OR its revision dates.
      const candidates = [d.due_date, d.revision_date_1, d.revision_date_2]
        .filter(Boolean)
        .map((v) => new Date(v))
        .filter((dt) => !Number.isNaN(dt.getTime()));

      if (candidates.length === 0) {
        matchesDate = false;
      } else {
        const latest = candidates.reduce((a, b) => (b > a ? b : a));
        const latestYMD = `${latest.getFullYear()}-${String(latest.getMonth() + 1).padStart(2, '0')}-${String(latest.getDate()).padStart(2, '0')}`;
        if (dateFrom && latestYMD < dateFrom) matchesDate = false;
        if (dateTo && latestYMD > dateTo) matchesDate = false;
      }
    }


    return matchesSearch && matchesAssigned && matchesStatus && matchesDate;
  });
}, [searchTerm, delegations, filterAssignedTo, filterStatus, dateFrom, dateTo]);

const displayStats = useMemo(() => {
  const total = filteredDelegations.length;
  const pending = filteredDelegations.filter(d => getTaskStatusKey(d.status) === 'pending').length;
  const completed = filteredDelegations.filter(d => getTaskStatusKey(d.status) === 'completed').length;

  const uniqueTasks = new Set(filteredDelegations.map(d => d.task_id));
  const duplicate_tasks = total - uniqueTasks.size;

  return { total, pending, completed, duplicate_tasks };
}, [filteredDelegations]);

  // const displayStats = useMemo(() => {
  //   const total = filteredDelegations.length;
  //   const pending = filteredDelegations.filter(d => (d.status || '').toLowerCase() === 'pending').length;
  //   const completed = filteredDelegations.filter(d => (d.status || '').toLowerCase() === 'completed').length;
    
  //   // Calculate duplicates by finding the difference between total rows and unique task_ids
  //   const uniqueTasks = new Set(filteredDelegations.map(d => d.task_id));
  //   const duplicate_tasks = total - uniqueTasks.size;

  //   return { total, pending, completed, duplicate_tasks };
  // }, [filteredDelegations]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'assigned_to') {
      const selectedEmployee = employees.find((emp) => String(emp.id) === String(value));
      setFormData(prev => ({
        ...prev,
        assigned_to: value,
        department_id: selectedEmployee?.dept_id ? String(selectedEmployee.dept_id) : '',
      }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  const selectedDate = formData.due_date ? new Date(formData.due_date) : new Date();

  const finalPayload = {
    task_id: generateTaskId(),
    task_description: formData.task_description,
    assigned_to: formData.assigned_to,
    assigned_by: user?.emp_id || user?.id || '',
    department_id: formData.department_id, // ADDED THIS
   updated_by: user?.emp_id || user?.id, // FIX: Explicitly send user name
    due_date: formData.due_date,
    week_no: getISOWeek(selectedDate),
    status: 'Pending',
    link: formData.link || null
  };

  try {
    const response = await fetch('/api/delegation-tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(finalPayload),
    });

    const data = await response.json();

    if (data.success) {
      toast.success('Delegation task created successfully!');
      setFormData({ task_description: '', assigned_to: '', department_id: '', due_date: '', link: '' });
      setActiveTab('list');
      await loadDelegations(); 
    } else {
      toast.error(data.message || 'Failed to create task');
    }
  } catch (error) {
    toast.error('Network error. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};
  const getRowColorClass = (del) => {
    // 1. If the database already has a color saved, use it!
    if (del.color) {
      return del.color;
    }

    // 2. Fallback logic for older tasks that don't have a color in the DB yet
    if (del.revision_date_2) return 'dt-row-red';
    if (del.revision_date_1) {
      const dueWeek = getISOWeek(new Date(del.due_date));
      const rev1Week = getISOWeek(new Date(del.revision_date_1));
      if (dueWeek !== rev1Week) return 'dt-row-red';
      return 'dt-row-yellow';
    }
    return 'dt-row-green';
  };

  const handleDoubleClick = (id, field, currentValue) => {
    const task = delegations.find((d) => d.delegation_id === id);
  if (!task) return;

  if (isLockedDuplicateTask(task)) {
    toast.error('This copied task is locked and cannot be edited.');
    return;
  }

    const currentUserId = String(user?.emp_id || user?.id);

    // --- NEW: Immediate User Feedback Validations ---

    // 1. Prevent editing revision dates if the task is already completed
    if ((task.status || '').toLowerCase() === 'completed' && (field === 'revision_date_1' || field === 'revision_date_2')) {
      toast.error('Cannot update revise date because the task is already completed.');
      return; // Stop the double-click action completely
    }

    // 2. Prevent changing status if deadlines have passed without proper revisions
    if (field === 'status' && (task.status || '').toLowerCase() !== 'completed') {
      const getLocalYMD = (d) => {
        if (!d) return null;
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return null;
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      };
      
      const todayYMD = getLocalYMD(new Date());
      const dueYMD = getLocalYMD(task.due_date);
      const rev1YMD = getLocalYMD(task.revision_date_1);

      if (dueYMD && dueYMD < todayYMD && !task.revision_date_1) {
        toast.error('You need to add a revise date because the due date has passed.');
        return; // Stop the double-click action completely
      }

      if (rev1YMD && rev1YMD < todayYMD && !task.revision_date_2) {
        toast.error('Revise date 2 needs to be added because revise date 1 has passed.');
        return; // Stop the double-click action completely
      }
    }
    // ------------------------------------------------

    // 3. Restrict core fields to Admin/MIS only
    const adminOnlyFields = [
      'revision_date_1',
      'revision_date_2',
      'task_description',
      'assigned_to',
      'due_date'
    ];

    if (adminOnlyFields.includes(field) && !isAdminRole(user)) {
      toast.error('Only administrators can modify this field');
      return;
    }

    // 4. Restrict reasons to the Assignee, Assigner, or Admin/MIS
    const reasonFields = ['reason_for_revision_1', 'reason_for_revision_2'];
    if (reasonFields.includes(field)) {
      const isAssignee = currentUserId === String(task.assigned_to);
      const isAssigner = currentUserId === String(task.assigned_by);
      
      if (!isAdminRole(user) && !isAssignee && !isAssigner) {
        toast.error('Only the assigned person or an administrator can modify the revision reason');
        return;
      }
    }

    // 5. Proceed with editing if all validations pass
    setEditingCell({ id, field });
    if (field.includes('date')) {
      setEditValue(currentValue ? new Date(currentValue).toISOString().split('T')[0] : '');
    } else {
      setEditValue(currentValue || '');
    }
  };

 const saveInlineEdit = async (id, field, valueOverride) => {
    const finalValue = valueOverride !== undefined ? valueOverride : editValue;
    
   const currentTask = delegations.find((d) => d.delegation_id === id);

if (isLockedDuplicateTask(currentTask)) {
  toast.error('This copied task is locked and cannot be edited.');
  setEditingCell({ id: null, field: null });
  return;
}
    
    // Helper to safely format dates to local YYYY-MM-DD to avoid timezone bugs
    const getLocalYMD = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return '';
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };

    if (currentTask) {
      let isSame = false;
      const originalValue = currentTask[field];
      
      if (field.includes('date')) {
        const origDate = getLocalYMD(originalValue);
        isSame = origDate === finalValue;
      } else {
        isSame = originalValue === finalValue || (!originalValue && !finalValue);
      }

      if (isSame) {
        setEditingCell({ id: null, field: null });
        return;
      }

      // --- VALIDATION: Completed tasks cannot have revision dates modified ---
      if ((currentTask.status || '').toLowerCase() === 'completed' && (field === 'revision_date_1' || field === 'revision_date_2')) {
        toast.error('Cannot update revise date when the task is already completed.');
        setEditingCell({ id: null, field: null });
        return;
      }

      // --- VALIDATION: Block status update if date has passed without proper revision ---
      if (field === 'status' && String(finalValue).toLowerCase() !== 'pending') {
        const todayYMD = getLocalYMD(new Date());
        const dueYMD = getLocalYMD(currentTask.due_date);
        const rev1YMD = getLocalYMD(currentTask.revision_date_1);

        if (dueYMD && dueYMD < todayYMD && !currentTask.revision_date_1) {
          toast.error('You need to add a revise date because the due date has passed.');
          setEditingCell({ id: null, field: null });
          return;
        }

        if (rev1YMD && rev1YMD < todayYMD && !currentTask.revision_date_2) {
          toast.error('Revise date 2 needs to be added because revise date 1 has passed.');
          setEditingCell({ id: null, field: null });
          return;
        }
      }
    }
    
    // Apply Optimistic Update
    setDelegations(prev => prev.map(del => 
      del.delegation_id === id ? { ...del, [field]: finalValue } : del
    ));
    setEditingCell({ id: null, field: null });

    try {
      const response = await fetch(`/api/delegation-tasks/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          [field]: finalValue || null,
         updated_by: user?.emp_id || user?.id
        }),
      });
      const data = await response.json();
      
      if (!data.success) {
        // Revert Optimistic Update if validation failed on the backend
        setDelegations(prev => prev.map(del => del.delegation_id === id ? currentTask : del));
        throw new Error(data.message);
      }
      
      // Handle the visual success states
      if (data.duplicated) {
        toast.success(data.message);
        // Revert the old task because it was purposely skipped by the backend
        setDelegations(prev => prev.map(del => del.delegation_id === id ? currentTask : del));
      } else {
        toast.success(`${field.includes('date') ? 'Revision date' : 'Task status'} updated successfully`);
      }
      
      await loadDelegations(true); 
      
    } catch (error) {
      toast.error(error.message || 'Failed to save update');
      await loadDelegations(true); 
    }
};

  const FilterRow = () => (
    <div className="dt-filters-bar">
      <div className="dt-filter-search">
        <SearchIcon />
        <input
          type="text"
          placeholder="Search by task ID, title or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      {/* --- NEW: Wrap this Assigned To filter in the Admin Check --- */}
      {isAdminRole(user) && (
        <div className="dt-filter-group">
          <label>Assigned To</label>
          <select value={filterAssignedTo} onChange={(e) => setFilterAssignedTo(e.target.value)}>
            <option value="All">All Employees</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.emp_name || emp.username || `Emp ${emp.id}`}
              </option>
            ))}
          </select>
        </div>
      )}
     <div className="dt-filter-group">
  <label>Status</label>
  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
    <option value="All">All Status</option>
    <option value="pending">Pending</option>
    <option value="completed">Completed</option>
    <option value="Shifted">Shifted</option>
    <option value="overdue">Overdue</option>
  </select>
</div>
      {/* Checklist-style range picker — filters by each task's LATEST date (due or revision) */}
      <div className="dt-filter-group" style={{ flex: '2', minWidth: '260px' }}>
        <label>Date Range (latest of due / revision)</label>
        <DelegationDateRangePicker
          startDate={dateFrom}
          endDate={dateTo}
          onDateChange={(start, end) => { setDateFrom(start); setDateTo(end); }}
        />
      </div>
      
      <div className="dt-filter-actions">
        <button 
          className="dt-btn-outline" 
          type="button" 
          onClick={() => { 
            setSearchTerm(''); 
            setFilterAssignedTo('All'); 
            setFilterStatus('All'); 
            setDateFrom(''); 
            setDateTo(''); 
          }}
        >
          Reset Filters
        </button>
      </div>
    </div>
  );

  // --- ADD THESE HANDLERS ---
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedTasks([]);
  };

  const handleSelectTask = (id) => {
    setSelectedTasks(prev => 
      prev.includes(id) ? prev.filter(taskId => taskId !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedTasks.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedTasks.length} task(s)? This action cannot be undone.`)) return;

    try {
      const response = await fetch('/api/delegation-tasks/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedTasks })
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        setIsSelectionMode(false);
        setSelectedTasks([]);
        loadDelegations(); // Refresh the list
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete tasks');
    }
  };

  return accessGranted ? (
  <div className="fms-shell">
    <AppSidebar
      user={user}
      active="delegation"
      onLogout={onLogout}
      loggingOut={loggingOut}
      onNavigateToDashboard={onNavigateToDashboard}
      onNavigateToTasks={onNavigateToTasks}
      onNavigateToFMS={onNavigateToFMS}
      onNavigateToReturns={onNavigateToReturns}
      onNavigateToDelegation={() => {}}
      onNavigateToAllTasks={onNavigateToAllTasks}
      onNavigateToResourceControl={onNavigateToResourceControl}
      onNavigateToLogging={onNavigateToLogging}
    />

    <main className="fms-main">
      <header className="fms-topbar rrl-topbar">
        <div className="mobile-left">
          <button className="mobile-menu-btn" type="button" aria-label="Open menu" />
        </div>
        {/* <div className="fms-global-search">
          <input
            type="search"
            placeholder="Search delegation tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div> */}
        <div className="topbar-actions">
            <div className="avatar-placeholder" style={{ background: getUserColor(user?.emp_name || user?.username) }}>
              {getInitials(user)}
            </div>
          </div>
      </header>

      <div className="fms-content dt-wrapper">
        <div className="dt-header-tabs">
  <button className={`dt-tab ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')} type="button">
    <UserIcon /> Active Delegations
  </button>
  {isAdminRole(user) && (
            <button className={`dt-tab ${activeTab === 'create' ? 'active' : ''}`} onClick={() => setActiveTab('create')} type="button">
              <PlusIcon /> Create New Task
            </button>
          )}

  {/* NEW: Admin Bulk Delete Actions */}
  {isAdminRole(user) && activeTab === 'list' && (
    <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
      {isSelectionMode ? (
        <>
          <button type="button" className="dt-btn-outline" onClick={toggleSelectionMode}>
            Cancel
          </button>
          <button 
            type="button" 
            className="dt-btn-primary" 
            style={{ background: '#ef4444' }} // Red background for destructive action
            disabled={selectedTasks.length === 0}
            onClick={handleDeleteSelected}
          >
            Delete Selected ({selectedTasks.length})
          </button>
        </>
      ) : (
        <button type="button" className="dt-btn-outline" onClick={toggleSelectionMode}>
          Select Tasks to Delete
        </button>
      )}
    </div>
  )}
</div>

        {activeTab === 'list' && (
           <>
        <div className="dt-metrics-grid">
  {/* TOTAL DELEGATIONS */}
  <div className="dt-metric-card style-blue">
    <div className="dt-metric-icon-box">
      <CubeIcon />
    </div>
    <div className="dt-metric-info">
      <span className="dt-metric-label">TOTAL DELEGATIONS</span>
      <span className="dt-metric-value">{displayStats.total}</span>
    </div>
  </div>

  {/* IN PROGRESS */}
  <div className="dt-metric-card style-purple">
    <div className="dt-metric-icon-box">
      <ClockIcon />
    </div>
    <div className="dt-metric-info">
      <span className="dt-metric-label">IN PROGRESS</span>
      <span className="dt-metric-value">{displayStats.pending}</span>
    </div>
  </div>

  {/* COMPLETED */}
  <div className="dt-metric-card style-green">
    <div className="dt-metric-icon-box">
      <CheckCircleIcon />
    </div>
    <div className="dt-metric-info">
      <span className="dt-metric-label">COMPLETED</span>
      <span className="dt-metric-value">{displayStats.completed}</span>
    </div>
  </div>

  {/* DUPLICATED / WARNING */}
  <div className="dt-metric-card style-yellow">
    <div className="dt-metric-icon-box">
      <WarningTriangleIcon />
    </div>
    <div className="dt-metric-info">
      <span className="dt-metric-label">DUPLICATED</span>
      <span className="dt-metric-value">{displayStats.duplicate_tasks}</span>
    </div>
  </div>
</div>

           {FilterRow()}

           {loading ? (
             <div className="dt-loading-state">Loading delegation tasks...</div>
           ) : (
             <div className="dt-table-responsive">
               <table className="dt-table">
                 
<thead>
  <tr>
    {isSelectionMode && <th style={{ width: '40px' }}>Select</th>}
    <th>Task ID</th>
    <th>Task Description</th>
    <th>Assigned To</th>
    <th>Assigned By</th>
    <th>Status (Dbl-Click)</th>
    <th>Due Date</th>
    <th>Rev 1 (Dbl-Click)</th>
    <th>Rev 1 Reason (Dbl-Click)</th>
    <th>Rev 2 (Dbl-Click)</th>
    <th>Rev 2 Reason (Dbl-Click)</th>
    <th>Week No</th>
    <th>Link (Dbl-Click)</th>
  </tr>
</thead>
          <tbody>
  {filteredDelegations.length > 0 ? (
    filteredDelegations.map((del) => {
      const currentUserId = String(user?.emp_id || user?.id);
      const canEditReason = isAdminRole(user) || currentUserId === String(del.assigned_to) || currentUserId === String(del.assigned_by);
      const isCompleted = (del.status || '').toLowerCase() === 'completed';
      const isLockedTaskRow = isLockedDuplicateTask(del);

      // --- NEW: Calculate Blocked States for UI Feedback ---
      const getLocalYMD = (d) => {
        if (!d) return null;
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return null;
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      };
      
      const todayYMD = getLocalYMD(new Date());
      const dueYMD = getLocalYMD(del.due_date);
      const rev1YMD = getLocalYMD(del.revision_date_1);

      const statusBlockedReason = (() => {
        if (isCompleted) return false; // Allowed to change back to pending
        if (dueYMD && dueYMD < todayYMD && !del.revision_date_1) return 'You need to add a revise date because the due date has passed.';
        if (rev1YMD && rev1YMD < todayYMD && !del.revision_date_2) return 'Revise date 2 needs to be added because revise date 1 has passed.';
        return false;
      })();
      // -----------------------------------------------------

      return (
        <tr key={del.delegation_id} className={`${getRowColorClass(del)}${isLockedTaskRow ? ' dt-row-locked' : ''}`}>
          
          {/* Checkbox for bulk delete */}
          {isSelectionMode && (
            <td style={{ textAlign: 'center' }}>
              <input 
                type="checkbox" 
                checked={selectedTasks.includes(del.delegation_id)}
                onChange={() => handleSelectTask(del.delegation_id)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
            </td>
          )}

          <td className="dt-cell-id">
  <strong>#{del.task_id}</strong>
  <span style={{ fontSize: '10px', color: '#64748b', display: 'block' }}>Sys ID: {del.delegation_id}</span>
  {isLockedTaskRow && (
    <span className="dt-lock-badge" title="This copied task is locked and cannot be edited">
      Locked
    </span>
  )}
            {del.revised_date_count > 1 && (
               <span style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600 }}>Dates: {del.revised_date_count}</span>
            )}
          </td>

          {/* Task Description Cell */}
          <td 
            className="dt-editable-cell dt-cell-desc"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'task_description', del.task_description)}
            style={{ cursor: isAdminRole(user) ? 'pointer' : 'not-allowed' }}
            title={isAdminRole(user) ? 'Double-click to edit (Admin only)' : ''}
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'task_description' ? (
              <textarea
                className="dt-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => saveInlineEdit(del.delegation_id, 'task_description')}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditingCell({ id: null, field: null });
                }}
                autoFocus
                style={{ minHeight: '60px', resize: 'vertical' }}
              />
            ) : (
              del.task_description
            )}
          </td>

          {/* UPDATED: Assigned To Cell - Custom Dropdown */}
          <td 
            className="dt-editable-cell"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'assigned_to', del.assigned_to)}
            style={{ cursor: isAdminRole(user) ? 'pointer' : 'not-allowed', position: 'relative' }}
            title={isAdminRole(user) ? 'Double-click to reassign (Admin only)' : ''}
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'assigned_to' ? (
              <div 
                className="dt-custom-dropdown-container"
                tabIndex={0}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setEditingCell({ id: null, field: null });
                  }
                }}
                ref={el => el && el.focus()}
              >
                <div className="dt-dropdown-menu" style={{ maxHeight: '250px', overflowY: 'auto', minWidth: '200px' }}>
                  {employees.map((emp) => (
                    <button 
                      key={emp.id}
                      className="dt-dropdown-item" 
                      onMouseDown={(e) => {
                        e.preventDefault();
                        saveInlineEdit(del.delegation_id, 'assigned_to', emp.id);
                      }}
                    >
                      <div className="dt-cell-user" style={{ gap: '8px' }}>
                        <div className="dt-avatar-circle" style={{ background: getAvatarColor(emp.emp_name || emp.username), width: 22, height: 22, fontSize: 9 }}>
                          {getAvatarInitials(emp.emp_name || emp.username)}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                          {emp.emp_name || emp.username}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="dt-cell-user">
                <div className="dt-avatar-circle" style={{ background: getAvatarColor(del.assigned_to_name), width: 26, height: 26, fontSize: 10 }}>
                  {getAvatarInitials(del.assigned_to_name)}
                </div>
                <div className="dt-cell-user-info">
                  <strong>{del.assigned_to_name}</strong>
                  {del.assigned_to_designation && <small>{del.assigned_to_designation}</small>}
                </div>
              </div>
            )}
          </td>

          <td>
            <div className="dt-cell-user">
              <div className="dt-avatar-circle" style={{ background: getAvatarColor(del.assigned_by_name), width: 26, height: 26, fontSize: 10 }}>
                {getAvatarInitials(del.assigned_by_name)}
              </div>
              <div className="dt-cell-user-info">
                <strong>{del.assigned_by_name}</strong>
                {del.assigned_by_designation && (
                  <small>{del.assigned_by_designation}</small>
                )}
              </div>
            </div>
          </td>

          <td 
            className="dt-editable-cell"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'status', del.status)}
            style={{ 
              position: 'relative',
              cursor: statusBlockedReason ? 'not-allowed' : 'pointer',
              opacity: statusBlockedReason ? 0.8 : 1
            }}
            title={statusBlockedReason || 'Double-click to change status'}
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'status' ? (
              <div 
                className="dt-custom-dropdown-container"
                tabIndex={0}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setEditingCell({ id: null, field: null });
                  }
                }}
                ref={el => el && el.focus()}
              >
                <div className="dt-dropdown-menu">
                  <button 
                    className="dt-dropdown-item status-pending" 
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveInlineEdit(del.delegation_id, 'status', 'Pending');
                    }}
                  >
                    <span style={{ visibility: del.status === 'Pending' ? 'visible' : 'hidden' }}>✓</span>
                    Pending
                  </button>
                  <button 
                    className="dt-dropdown-item status-completed" 
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveInlineEdit(del.delegation_id, 'status', 'Completed');
                    }}
                  >
                    <span style={{ visibility: del.status === 'Completed' ? 'visible' : 'hidden' }}>✓</span>
                    Completed
                  </button>
                </div>
              </div>
            ) : (
              <span className={`dt-badge ${getStatusBadgeClass(del.status)}`}>
                {del.status}
              </span>
            )}
          </td>

          {/* UPDATED: Due Date Cell - Custom Calendar */}
          <td 
            className="dt-editable-cell"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'due_date', del.due_date)}
            style={{ whiteSpace: 'nowrap', fontWeight: '600', cursor: isAdminRole(user) ? 'pointer' : 'not-allowed', position: 'relative' }}
            title={isAdminRole(user) ? 'Double-click to edit (Admin only)' : ''}
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'due_date' ? (
              <div 
                className="dt-calendar-wrapper" 
                tabIndex={0} 
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setEditingCell({ id: null, field: null });
                  }
                }} 
                ref={el => el && el.focus()}
              >
                <CustomCalendar
                  value={editValue}
                  onSelect={(newDateStr) => {
                    setEditValue(newDateStr);
                    saveInlineEdit(del.delegation_id, 'due_date', newDateStr); 
                  }}
                />
              </div>
            ) : (
              formatDate(del.due_date)
            )}
          </td>

         <td 
            className="dt-editable-cell"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'revision_date_1', del.revision_date_1)}
            style={{ 
              position: 'relative',
              cursor: isCompleted ? 'not-allowed' : (isAdminRole(user) ? 'pointer' : 'not-allowed'),
              opacity: (isCompleted || !isAdminRole(user)) ? 0.7 : 1
            }}
            title={isCompleted ? 'Cannot edit: Task is completed' : (isAdminRole(user) ? 'Double-click to edit (Admin only)' : 'Editing disabled - Admin only')}
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'revision_date_1' ? (
              <div 
                className="dt-calendar-wrapper" 
                tabIndex={0} 
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setEditingCell({ id: null, field: null });
                  }
                }} 
                ref={el => el && el.focus()}
              >
                <CustomCalendar
                  value={editValue}
                  minDate={getMinRevisionDate(del.due_date)}
                  onSelect={(newDateStr) => {
                    setEditValue(newDateStr);
                    saveInlineEdit(del.delegation_id, 'revision_date_1', newDateStr); 
                  }}
                />
              </div>
            ) : (
              <div className="dt-stat-item">
                <strong>{formatDate(del.revision_date_1)}</strong>
              </div>
            )}
          </td>

          <td 
            className="dt-editable-cell"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'reason_for_revision_1', del.reason_for_revision_1)}
            style={{ 
              position: 'relative',
              cursor: canEditReason ? 'pointer' : 'not-allowed',
              maxWidth: '200px',
              opacity: canEditReason ? 1 : 0.7
            }}
            title={canEditReason ? "Double-click to edit reason" : "Editing restricted to assigned person or admin"}
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'reason_for_revision_1' ? (
              <textarea
                className="dt-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => saveInlineEdit(del.delegation_id, 'reason_for_revision_1')}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditingCell({ id: null, field: null });
                }}
                autoFocus
                style={{ minHeight: '60px', resize: 'vertical' }}
              />
            ) : (
              <span style={{ fontSize: '12px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                {del.reason_for_revision_1 || '-'}
              </span>
            )}
          </td>
          
          <td 
            className="dt-editable-cell"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'revision_date_2', del.revision_date_2)}
            style={{ 
              position: 'relative',
              cursor: isAdminRole(user) ? 'pointer' : 'not-allowed',
              opacity: isAdminRole(user) ? 1 : 0.7
            }}
            title={isAdminRole(user) ? 'Double-click to edit (Admin only)' : 'Editing disabled - Admin only'}
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'revision_date_2' ? (
              <div 
                className="dt-calendar-wrapper" 
                tabIndex={0} 
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setEditingCell({ id: null, field: null });
                  }
                }} 
                ref={el => el && el.focus()}
              >
                <CustomCalendar
                  value={editValue}
                  minDate={getMinRevisionDate(del.revision_date_1, del.due_date)}
                  onSelect={(newDateStr) => {
                    setEditValue(newDateStr);
                    saveInlineEdit(del.delegation_id, 'revision_date_2', newDateStr);
                  }}
                />
              </div>
            ) : (
              <div className="dt-stat-item">
                <strong>{formatDate(del.revision_date_2)}</strong>
              </div>
            )}
          </td>
          
          <td 
            className="dt-editable-cell"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'revision_date_2', del.revision_date_2)}
            style={{ 
              position: 'relative',
              cursor: isCompleted ? 'not-allowed' : (isAdminRole(user) ? 'pointer' : 'not-allowed'),
              opacity: (isCompleted || !isAdminRole(user)) ? 0.7 : 1
            }}
            title={isCompleted ? 'Cannot edit: Task is completed' : (isAdminRole(user) ? 'Double-click to edit (Admin only)' : 'Editing disabled - Admin only')}
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'reason_for_revision_2' ? (
              <textarea
                className="dt-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => saveInlineEdit(del.delegation_id, 'reason_for_revision_2')}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditingCell({ id: null, field: null });
                }}
                autoFocus
                style={{ minHeight: '60px', resize: 'vertical' }}
              />
            ) : (
              <span style={{ fontSize: '12px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                {del.reason_for_revision_2 || '-'}
              </span>
            )}
          </td>
                                     
          <td style={{ textAlign: 'center', fontWeight: '800', color: '#475569' }}>
            {del.week_no || '-'}
          </td>
          
          <td 
            className="dt-editable-cell"
            onDoubleClick={() => handleDoubleClick(del.delegation_id, 'link', del.link)}
            style={{ 
              position: 'relative',
              cursor: 'pointer',
              maxWidth: '250px'
            }}
            title="Double-click to edit link"
          >
            {editingCell.id === del.delegation_id && editingCell.field === 'link' ? (
              <input
                type="text"
                className="dt-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => saveInlineEdit(del.delegation_id, 'link')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveInlineEdit(del.delegation_id, 'link');
                  if (e.key === 'Escape') setEditingCell({ id: null, field: null });
                }}
                placeholder="Enter URL..."
                autoFocus
              />
            ) : (
              del.link ? (
                <a 
                  href={del.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    color: '#0f766e',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}
                  title={del.link}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  Open
                </a>
              ) : (
                <span style={{ color: '#cbd5e1', fontSize: '12px' }}>-</span>
              )
            )}
          </td>
        </tr>
      );
    })
  ) : (
    <tr>
      <td colSpan={isSelectionMode ? 13 : 12} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
        No delegation tasks found.
      </td>
    </tr>
  )}
</tbody>
               </table>
             </div>
           )}
         </>
        )}

        {activeTab === 'create' && (
          <div className="dt-form-container">
            <form className="dt-modular-form" onSubmit={handleSubmit}>
              <div className="dt-form-sections-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                
                <div className="dt-form-section">
                  <div className="dt-section-header">
                    <DocIcon />
                    <h3>Task Info</h3>
                  </div>
                  
                  <div className="dt-form-group">
                    <label>
                      Description <span>*</span>
                    </label>
                    <textarea
                      name="task_description"
                      value={formData.task_description}
                      onChange={handleInputChange}
                      placeholder="Enter task description..."
                      rows="8"
                      required
                    />
                  </div>
                </div>

                <div className="dt-form-section dt-form-section-last">
                  <div className="dt-section-header">
                    <UserIcon />
                    <h3>Assignments & Timeline</h3>
                  </div>
                  
                  <div className="dt-form-group">
                    <label>
                      Assigned To <span>*</span>
                    </label>
                    <select
                      name="assigned_to"
                      value={formData.assigned_to}
                      onChange={handleInputChange}
                      required
                      disabled={employeesLoading}
                    >
                      <option value="">{employeesLoading ? 'Loading employees...' : 'Select employee'}</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {getEmployeeLabel(emp)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="dt-form-group">
                    <label>
                      Due Date <span>*</span>
                    </label>
                    <div className="dt-input-icon">
                      <CalendarIcon />
                      <input
                        type="datetime-local"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="dt-form-group">
                    <label>
                      Department <span>*</span>
                    </label>
                    <select
                      name="department_id"
                      value={formData.department_id}
                      onChange={handleInputChange}
                      required
                      disabled={departmentsLoading}
                    >
                      <option value="">{departmentsLoading ? 'Loading departments...' : 'Select department'}</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.dept_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                </div>
              </div>

              <div className="dt-form-actions">
                <button type="button" className="dt-btn-secondary" onClick={() => setActiveTab('list')}>
                  Cancel
                </button>
                <button type="submit" className="dt-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create Delegation'}
                  <span className="dt-btn-arrow">↗</span>
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </main>
  </div>
) : (
  <div className="fms-shell">
    <AppSidebar
      user={user}
      active="delegation"
      onLogout={onLogout}
      loggingOut={loggingOut}
      onNavigateToDashboard={onNavigateToDashboard}
      onNavigateToTasks={onNavigateToTasks}
      onNavigateToFMS={onNavigateToFMS}
      onNavigateToReturns={onNavigateToReturns}
      onNavigateToDelegation={() => {}}
      onNavigateToAllTasks={onNavigateToAllTasks}
      onNavigateToResourceControl={onNavigateToResourceControl}
      onNavigateToLogging={onNavigateToLogging}
    />

    <main className="fms-main" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh'
    }}>
      <div style={{
        background: '#fef3c7',
        border: '1px solid #fcd34d',
        borderRadius: '8px',
        padding: '32px 40px',
        maxWidth: '500px',
        textAlign: 'center'
      }}>
        <h2 style={{
          margin: '0 0 12px 0',
          fontSize: '20px',
          fontWeight: '700',
          color: '#92400e'
        }}>
          🔒 Access Denied
        </h2>
        <p style={{
          margin: '0 0 20px 0',
          fontSize: '14px',
          color: '#b45309',
          lineHeight: '1.6'
        }}>
          You don't have access to Delegation Tasks. Please contact your administrator to request access.
        </p>
        <button
          type="button"
          onClick={onNavigateToDashboard}
          style={{
            background: '#1f5f55',
            color: 'white',
            border: 'none',
            padding: '10px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Back to Dashboard
        </button>
      </div>
    </main>
  </div>
);
}
